
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
*/
import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { XIcon, LockIcon } from './icons';
import { useTranslation } from '../i18n/useTranslation';
import Spinner from './Spinner';

interface PasswordPromptModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (password: string) => Promise<boolean>;
  username: string;
}

const PasswordPromptModal: React.FC<PasswordPromptModalProps> = ({ isOpen, onClose, onSubmit, username }) => {
    const { t } = useTranslation();
    const [password, setPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        setError(null);
        
        try {
            const success = await onSubmit(password);
            if (!success) {
                setError("Incorrect password. Please try again.");
            } else {
                setPassword('');
            }
        } catch (e) {
            setError("Authentication failed");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={onClose}
                    className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4"
                >
                    <motion.div
                        initial={{ scale: 0.95, y: 20 }}
                        animate={{ scale: 1, y: 0 }}
                        exit={{ scale: 0.95, y: 20 }}
                        onClick={(e) => e.stopPropagation()}
                        className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-6 w-full max-w-sm border border-gray-200 dark:border-gray-700 relative overflow-hidden"
                    >
                        <button 
                            onClick={onClose} 
                            className="absolute top-4 right-4 p-1 rounded-full text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                            aria-label="Close"
                        >
                            <XIcon className="w-5 h-5" />
                        </button>

                        <div className="flex flex-col items-center mb-6 text-center">
                            <div className="w-12 h-12 bg-indigo-100 dark:bg-indigo-900/30 rounded-full flex items-center justify-center mb-3">
                                <LockIcon className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                                Authenticate Access
                            </h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                                Enter password for <strong>{username}</strong>
                            </p>
                        </div>

                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div>
                                <input 
                                    type="password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="Password"
                                    className="w-full p-3 border border-gray-300 rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-indigo-500 outline-none transition-all text-center tracking-widest"
                                    autoFocus
                                    disabled={isLoading}
                                />
                                {error && <p className="text-red-500 text-xs mt-2 text-center font-medium animate-pulse">{error}</p>}
                            </div>
                            
                            <button 
                                type="submit" 
                                disabled={isLoading || !password}
                                className="w-full bg-gray-900 text-white font-bold py-3 rounded-lg hover:bg-gray-800 dark:bg-indigo-600 dark:hover:bg-indigo-500 transition-colors flex items-center justify-center disabled:opacity-50"
                            >
                                {isLoading ? <Spinner className="w-5 h-5" /> : 'Unlock'}
                            </button>
                        </form>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default PasswordPromptModal;
